import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { fetchObservation } from "services/dropdown";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";

const InvestigationWiseObservationDropdown = ({
  errors,
  control,
  setValue,
  getValues,
  watch,
  rowData,
  mappedData,
  required,
}) => {
  const observationName = getValues()?.observation;
  const selectedObservation = watch("observation_id");

  // Get investigation_id from form values or rowData
  const investigationId =
    getValues()?.investigation_id ||
    rowData?.investigation_obj?.id ||
    rowData?.investigation_id ||
    0;

  // Prepare payload with investigation_id
  const payload = investigationId ? { investigation_id: investigationId } : {};

  const {
    data: Observations,
    refetch,
    isLoading,
    error,
  } = useQuery(
    ["ObservationDropdown", investigationId],
    () => fetchObservation(payload),
    {
      enabled: investigationId !== null && investigationId !== 0, // Only fetch when investigationId is available
      keepPreviousData: false,
      staleTime: DROPDOWN_STALE_TIME,
    }
  );

  const optionsList = useMemo(
    () =>
      Observations?.data?.data?.map((observation) => ({
        value: observation?.id,
        label: observation?.observation,
      })) || [],
    [Observations]
  );

  const options = mappedData
    ? optionsList?.filter((ele) => !mappedData.includes(ele.label))
    : optionsList;

  useEffect(() => {
    if (rowData && options.length) {
      const selectedOption = options.find(
        (option) => option.value === rowData?.observation_id
      );
      if (selectedOption) {
        setValue("observation_id", selectedOption?.value);
        setValue("observation", selectedOption?.label);
      }
    }
  }, [rowData, options, setValue]);

  // Clear observation when investigation changes
  useEffect(() => {
    if (investigationId !== (rowData?.investigation_id || 0)) {
      setValue("observation_id", null);
      setValue("observation", "");
    }
  }, [investigationId]);

  const selectedOption = useMemo(() => {
    return (
      options.find((option) => option.value === selectedObservation) ||
      options.find((option) => option.label === observationName) ||
      null
    );
  }, [options, selectedObservation, observationName]);

  // Only show loading when investigation is selected and data is loading
  if (investigationId && isLoading) return <div>Loading...</div>;
  if (investigationId && error) return <div>Error loading observations</div>;

  return (
    <>
      <Controller
        name="observation_id"
        control={control}
        rules={{
          required: required ? "Observation is required" : false,
          validate: (value) => {
            if (required && !value) return "Observation is required";
            return true;
          },
        }}
        render={({ field }) => (
          <Select
            {...field}
            options={options}
            isClearable
            placeholder={
              !investigationId
                ? "First select Investigation"
                : "Select Observation Name"
            }
            value={selectedOption || null}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base) => ({
                ...base,
                height: "42px",
              }),
              option: (base, { isFocused, isSelected }) => ({
                ...base,
              }),
            }}
            onChange={(selectedOption) => {
              const value = selectedOption ? selectedOption.value : "";
              field.onChange(value);
              setValue("observation_id", value);
              setValue("observation", selectedOption?.label || "");
            }}
            className={`react-select-container ${
              errors.observation_id ? "is-invalid" : ""
            }`}
            isDisabled={!investigationId} // Disable if no investigation selected
          />
        )}
      />
      {errors.observation_id && (
        <div className="invalid-feedback">{errors.observation_id.message}</div>
      )}
    </>
  );
};

export default InvestigationWiseObservationDropdown;
